import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

// Terms of sale (salgsvilkår) page. Required by Vipps for selling online — sections follow
// vippsmobilepay.com/legal/krav-til-nettside: parter, betaling, levering, angrerett, retur,
// reklamasjonshåndtering, konfliktløsning, plus membership-specific cancellation terms.
// We only sell Medlemskap (membership), so kurs/billetter/utleie/abonnement are not covered.

const COMPANY_NAME = 'Annaipoopathi aktivitets- og kultursenter (APTAK)';
const ORG_NUMBER = '937 718 659';
const CONTACT_EMAIL = 'kontakt@annaipoopathi.no';
const POSTAL_ADDRESS = 'Liaveien 6B, 1476 Rasta';

const SalgsvilkaarView: React.FC<RouteComponentProps> = () => {
  return (
    <div className="ods-margin-top-25 ods-grid ods-grid--gap ods-grid--center">
      <div className="ods-grid__column--12 ods-grid__column--start-3-breakpoint-medium ods-grid__column--end-11-breakpoint-medium">
        <div className="ods-status-card">
          <div className="ods-status-card__content">
            <h1 className="ods-status-card__heading">Salgsvilkår</h1>
            <p>
              Disse salgsvilkårene gjelder for kjøp av medlemskap (medlemskontingent) hos{' '}
              {COMPANY_NAME} via denne nettsiden. Betaling skjer med Vipps. Vilkårene utgjør
              sammen med din bestilling avtalen mellom deg som kjøper og oss som selger.
            </p>

            <h3 className="ods-margin-top-11">1. Parter</h3>
            <p>
              Selger er:
            </p>
            <ul>
              <li>{COMPANY_NAME}</li>
              <li>Organisasjonsnummer: {ORG_NUMBER}</li>
              <li>Adresse: {POSTAL_ADDRESS}</li>
              <li>
                E-post:{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="o-kom-link">{CONTACT_EMAIL}</a>
              </li>
            </ul>
            <p>
              Kjøper er den personen som er oppgitt som registrert bruker (forelder/foresatt)
              ved bestillingen.
            </p>

            <h3 className="ods-margin-top-11">2. Produkt og pris</h3>
            <p>
              Vi selger <strong>medlemskap</strong> for barn. Medlemskontingenten er{' '}
              <strong>100 kr per barn per år</strong>. Prisen er oppgitt inkludert eventuelle
              avgifter. Som betalende medlem får barnet tilgang til tamilskolen og til
              aktivitetene og arrangementene våre gjennom året (blant annet Thai Pongal,
              friidrett, tamileksamen og Aandu Vila).
            </p>

            <h3 className="ods-margin-top-11">3. Betaling</h3>
            <p>
              Betaling skjer med <strong>Vipps</strong> ved bestilling. Medlemskapet regnes som
              kjøpt når betalingen er gjennomført og bekreftet. Du mottar bekreftelse på kjøpet,
              og kjøpet registreres på din brukerprofil.
            </p>

            <h3 className="ods-margin-top-11">4. Levering</h3>
            <p>
              Medlemskapet er en digital tjeneste. Tilgangen aktiveres automatisk og umiddelbart
              etter at betalingen er bekreftet, og gjelder for det inneværende medlemsåret. Det
              sendes ingen fysiske varer.
            </p>

            <h3 className="ods-margin-top-11">5. Angrerett</h3>
            <p>
              Ved kjøp som forbruker har du <strong>14 dagers angrerett</strong> etter
              angrerettloven, regnet fra den dagen avtalen ble inngått. For å bruke angreretten
              kan du gi oss melding på{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="o-kom-link">{CONTACT_EMAIL}</a>{' '}
              innen fristen. Du kan bruke{' '}
              <a
                href="https://www.forbrukertilsynet.no/angrerettskjema"
                target="_blank"
                rel="noopener noreferrer"
                className="o-kom-link"
              >
                Forbrukertilsynets angreskjema
              </a>, men det er ikke et krav.
            </p>
            <p>
              Dersom du uttrykkelig har bedt om at tjenesten/medlemskapet skal tas i bruk før
              angrefristen utløper, kan angreretten falle bort når tjenesten er fullt levert, og
              du kan måtte betale for den delen av tjenesten som allerede er levert.
            </p>

            <h3 className="ods-margin-top-11">6. Retur og refusjon</h3>
            <p>
              Benytter du angreretten innen fristen, og medlemskapet ikke er tatt i bruk,
              refunderer vi det innbetalte beløpet uten unødig opphold og senest innen 14 dager
              etter at vi har mottatt meldingen din. Refusjon skjer via Vipps til samme konto
              som ble brukt ved kjøpet.
            </p>

            <h3 className="ods-margin-top-11">7. Endring og oppsigelse av medlemskap</h3>
            <p>
              Medlemskapet gjelder for ett medlemsår og fornyes ikke automatisk – det påløper
              ingen videre betaling før du eventuelt kjøper medlemskap på nytt. Ønsker du å endre
              eller si opp medlemskapet, eller du har spørsmål om medlemskapet ditt, kan du
              kontakte oss på{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="o-kom-link">{CONTACT_EMAIL}</a>.
            </p>

            <h3 className="ods-margin-top-11">8. Reklamasjon</h3>
            <p>
              Dersom det er feil eller mangler ved tjenesten, kan du reklamere ved å kontakte oss
              på{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="o-kom-link">{CONTACT_EMAIL}</a>.
              Reklamasjon bør skje innen rimelig tid etter at du oppdaget mangelen. Dine
              rettigheter følger av forbrukerkjøpsloven.
            </p>

            <h3 className="ods-margin-top-11">9. Konfliktløsning</h3>
            <p>
              Vi oppfordrer deg til å kontakte oss først dersom du har en klage, slik at vi kan
              forsøke å løse saken. Kommer vi ikke til enighet, kan du bringe saken inn for{' '}
              <a
                href="https://www.forbrukertilsynet.no/"
                target="_blank"
                rel="noopener noreferrer"
                className="o-kom-link"
              >
                Forbrukertilsynet
              </a>{' '}
              og videre til Forbrukerklageutvalget.
            </p>

            <h3 className="ods-margin-top-11">Kontakt</h3>
            <p>
              Spørsmål om kjøp og salgsvilkår kan rettes til{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="o-kom-link">{CONTACT_EMAIL}</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(SalgsvilkaarView);
